import React, { useState } from 'react'
import { Icon, IconButton } from '@shopflo/ui'
import { Cube, Trash } from '@phosphor-icons/react'
import { CatalogueDataType } from 'src/lib/types/common/catalogue'
import FormErrorText from 'src/components/common/FormErrorText'
import VariantEditorDialog from 'src/components/common/product-selector/VariantEditorDialog'
import { useLocale } from 'src/lib/hooks/useLocal'
import { classNames } from 'src/lib/utils/helpers'
import { QuantityDistributionErrorType } from 'src/lib/utils/automationsHelpers'

export interface CatalogueItemsListProps {
  data: {
    items: CatalogueDataType[]
    groupIndex: number
    displayGroup: CatalogueDataType[]
  }
  actions: {
    onRemoveItem: (groupIndex: number, itemIndex: number) => void
    onUpdateItem?: (updatedItem: CatalogueDataType, groupIndex: number, itemIndex: number) => void
  }

  errorHandling: {
    errors?: QuantityDistributionErrorType[]
  }
  config?: {
    showVariantEditor?: boolean
  }
  /** Optional extra content rendered right before the variants label for a given item, e.g. a conflict warning icon. */
  renderItemPrefix?: (item: CatalogueDataType, itemIndex: number) => React.ReactNode
}

const CatalogueItemsList: React.FC<CatalogueItemsListProps> = ({
  data: { items, groupIndex, displayGroup },
  actions: { onRemoveItem, onUpdateItem },
  errorHandling: { errors },
  config: { showVariantEditor = true } = {},
  renderItemPrefix
}) => {
  const { t } = useLocale()
  const [editingVariant, setEditingVariant] = useState<{ groupIndex: number; itemIndex: number } | null>(null)

  const handleApplyVariantUpdate = (updatedProduct: CatalogueDataType | null) => {
    if (editingVariant && onUpdateItem) {
      if (updatedProduct) {
        onUpdateItem(updatedProduct, editingVariant.groupIndex, editingVariant.itemIndex)
      } else {
        onRemoveItem(editingVariant.groupIndex, editingVariant.itemIndex)
      }
    }
    setEditingVariant(null)
  }

  const getEditingProductData = (): CatalogueDataType | null => {
    if (!editingVariant) return null
    const group = displayGroup
    if (!group) return null
    const item = group[editingVariant.itemIndex]
    if (!item) return null
    return item
  }

  return (
    <>
      {items.map((item, localIndex) => {
        const groupItems = displayGroup || []
        const originalIndex = groupItems.findIndex(i => i.id === item.id && i.type === item.type)
        const iIdx = originalIndex !== -1 ? originalIndex : localIndex

        const itemError = errors?.[groupIndex]?.selectedItems?.message
          ? errors[groupIndex].selectedItems.message
          : undefined

        return (
          <div
            id={`catalogue-item-${groupIndex}-${iIdx}`}
            key={`${groupIndex}-${iIdx}`}
            className='flex flex-col w-full'
          >
            <div
              className={classNames(`flex items-start justify-between py-1`, itemError ? 'bg-red-50 rounded px-2' : '')}
            >
              <div className='flex items-start gap-3 min-w-0 flex-1'>
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt='' className='w-9 h-9 rounded object-cover flex-shrink-0' />
                ) : (
                  <div className='h-9 w-9 p-2 border-1 rounded-xl flex items-center justify-center bg-zinc-50 flex-shrink-0'>
                    <Icon IconComponent={Cube} size='sm' className='text-zinc-400' iconProps={{ weight: 'duotone' }} />
                  </div>
                )}
                <div className='flex flex-col gap-0.5 justify-center min-w-0 min-h-8 flex-1 pr-8'>
                  <span className='text-text-sm-regular line-clamp-2 text-zinc-800'>
                    {item.description || 'Default Title'}
                  </span>
                </div>
              </div>

              <div className='flex items-center gap-3 min-h-8'>
                <div className='flex items-center gap-4'>
                  {renderItemPrefix?.(item, localIndex)}
                  {item.variants && item.variants.length > 0 && !showVariantEditor && (
                    <span className='text-text-xs-regular text-zinc-500'>
                      {t('discounts_v2.variants_count', {
                        count: item.variants.length,
                        total: item.totalVariantCount
                      })}
                    </span>
                  )}
                  {item.variants && item.variants.length > 0 && showVariantEditor && onUpdateItem && (
                    <span
                      id={`edit-variants-link-${groupIndex}-${iIdx}`}
                      className='text-xs text-zinc-500 underline underline-offset-4 decoration-zinc-200 cursor-pointer'
                      onClick={() => setEditingVariant({ groupIndex, itemIndex: iIdx })}
                    >
                      {t('discounts_v2.variants_count', {
                        count: item.variants.length,
                        total: item.totalVariantCount
                      })}
                    </span>
                  )}
                </div>

                <IconButton
                  id={`remove-item-button-${groupIndex}-${iIdx}`}
                  onClick={() => onRemoveItem(groupIndex, iIdx)}
                  variant='ghost'
                  size='xs'
                  IconComponent={Trash}
                  className='text-zinc-500'
                />
              </div>
            </div>
            {itemError && <FormErrorText message={itemError} />}
          </div>
        )
      })}

      {editingVariant && (
        <VariantEditorDialog
          open={!!editingVariant}
          onOpenChange={open => !open && setEditingVariant(null)}
          product={getEditingProductData()!}
          onApply={handleApplyVariantUpdate}
        />
      )}
    </>
  )
}

export default CatalogueItemsList
